<script setup lang="ts">
type ServiceIcon =
  | "plane"
  | "compass"
  | "utensils"
  | "clock"
  | "sparkles"
  | "map-pin";

interface ServiceCardItem {
  icon: ServiceIcon;
  title: string;
  caption: string;
}

interface ServiceCardsBlock {
  eyebrow?: string;
  header: string;
  intro?: string;
  items: ServiceCardItem[];
}

defineProps<{
  block: ServiceCardsBlock;
}>();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />

    <div
      class="relative mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 md:px-8 md:py-12"
    >
      <header data-reveal class="mb-8 max-w-3xl md:mb-10">
        <span
          v-if="block.eyebrow"
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ block.eyebrow }}
        </span>
        <h2
          class="mt-3 font-display text-3xl italic leading-tight text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-4 font-body text-base leading-relaxed text-slate-600 sm:text-lg lg:text-xl dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <div
        class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3"
      >
        <article
          v-for="(item, i) in block.items"
          :key="item.title"
          :data-reveal="''"
          :data-reveal-delay="String(i % 4)"
          class="group flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md sm:p-7 dark:border-slate-700 dark:bg-slate-800/70"
        >
          <span
            class="flex size-12 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
          >
            <Icon
              :name="`lucide:${item.icon}`"
              class="size-6"
              aria-hidden="true"
            />
          </span>
          <h3
            class="font-display text-lg italic leading-snug text-slate-900 sm:text-xl dark:text-white break-words"
          >
            {{ item.title }}
          </h3>
          <p
            class="font-body text-sm leading-relaxed text-slate-600 sm:text-base dark:text-slate-400"
          >
            {{ item.caption }}
          </p>
        </article>
      </div>
    </div>
  </section>
</template>
